import { useState } from "react";
import { Mail, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { submitEmail } from "../lib/api";
import { useAccess } from "../lib/access";
import { trackEvent } from "../lib/analytics";
import { toast } from "sonner";

/**
 * EmailGate — the soft early-traction gate.
 *
 * Drop-in replacement for `<AccessGate>` while we're still collecting early
 * email signups. Once the user submits a valid email we:
 *   1. POST it to `/api/email-signup` (server-side dedupe, source-tagged)
 *   2. Grant local access (`useAccess().grant`) so they're never asked again on this device
 *   3. Render the children
 *
 * When traction grows and we want to gate behind a real purchase again, just
 * swap this back to `<AccessGate>` in MealPlanner.jsx + CreateBuddy.jsx.
 */
export default function EmailGate({ feature, title, blurb, children, source }) {
  const { entitlements, grant } = useAccess();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  if (entitlements?.[feature]) return children;

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed.includes("@")) {
      toast.error("Enter a valid email to continue.");
      return;
    }
    setLoading(true);
    try {
      // Best-effort signup — even if the API is down we still let them in,
      // because gating behind a network call would be a worse UX than logging
      // a stray email server-side. The unlock is a UI state, not a paywall.
      try {
        await submitEmail(trimmed, source || feature);
      } catch (err) {
        console.warn("[email-gate] signup non-blocking failure:", err);
      }
      grant({
        email: trimmed,
        entitlements: { planner: true, generator: true },
      });
      trackEvent("funnel_unlock", { source: source || feature, feature });
      toast.success("You're in! Have fun building.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid={`gate-${feature}`} className="min-h-[70vh] bg-pb-cream py-16 px-5 sm:px-8">
      <div className="max-w-2xl mx-auto bg-white rounded-[2.5rem] shadow-[0_30px_80px_rgba(28,22,40,0.12)] border-4 border-white p-8 sm:p-12 text-center relative overflow-hidden">
        <div className="blob bg-pb-coral" style={{ width: 260, height: 260, top: -80, right: -80, opacity: 0.18 }} />
        <div className="blob bg-pb-sun" style={{ width: 200, height: 200, bottom: -80, left: -60, opacity: 0.25 }} />

        <div className="relative z-10">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-pb-sun text-pb-ink shadow-lg">
            <Sparkles className="h-7 w-7" />
          </div>
          <p className="mt-5 text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            Early Access · Free for now
          </p>
          <h1 className="font-display font-black text-3xl sm:text-4xl mt-2 leading-tight">
            {title}
          </h1>
          <p className="mt-3 text-pb-soft text-base leading-relaxed max-w-md mx-auto">
            {blurb}
          </p>
          <p className="mt-2 text-pb-soft text-sm">
            Drop your email to unlock — we'll send you early-access perks and one short note per month. No spam, ever.
          </p>

          <form onSubmit={onSubmit} data-testid={`gate-${feature}-form`} className="mt-7 flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <div className="flex-1 relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-pb-coral" />
              <input
                data-testid={`gate-${feature}-email`}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@family.com"
                className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream pl-11 pr-4 py-3 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition text-sm"
              />
            </div>
            <button
              data-testid={`gate-${feature}-submit`}
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-pb-coral text-white font-extrabold px-5 py-3 hover:scale-105 transition disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Unlock
            </button>
          </form>

          <p className="text-[11px] text-pb-soft mt-4">
            We'll never sell your email · unsubscribe anytime
          </p>
        </div>
      </div>
    </section>
  );
}
